import { useApesStorageApi } from "@/api/useApesStorageApi";
import { useSentry } from "@/composables/useSentry";
import { useToast } from "@/composables/useToast";
import { fetchApes } from "@/functions/fetchCollection/fetchApes";
import { fetchApeTraits } from "@/functions/fetchCollection/fetchApeTraits";
import { fetchCollection } from "@/functions/fetchCollection/fetchCollection";
import { captureError } from "@/helpers/captureError";
import { defineStore } from "pinia";
import { useMethBankStore } from "./meth-bank";
import { useWeb3Store } from "./web3";

export const useWalletCollectionStore = defineStore("wallet-collection", {
  state: () => ({
    genesis: [],
    gods: [],
    teens: [],
    orbs: [],
    incubators: [],
    methMachines: [],
    teenResurrections: [],
    breedingReplenishments: [],
    botbOG: [],
    botbEVO: [],
    botbBears: [],
    partners: {},
    apes: [],
    apeTraits: [],
    isLoading: true,
  }),
  getters: {
    apeAttachmentsMap() {
      const attachmentMap = {};
      this.apes.forEach((ape) => {
        if (!ape.attachment) return;
        const { contract_address, token_id } = ape.attachment;
        attachmentMap[`${contract_address}_${token_id}`] = ape.id;
      });
      return attachmentMap;
    },
    botbSetsIds() {
      const ids = this.botbOG
        .filter((token) => token.isInSet)
        .map((token) => token.id);

      return ids;
    },
    botbSetsTokens() {
      return this.botbSetsIds.map((id) => {
        const OG = this.botbOG.find((token) => token.id == id);
        const Bears = this.botbBears.find((token) => token.id == id);
        const EVO = this.botbEVO.find((token) => token.id == id);
        return {
          id,
          OG,
          Bears,
          EVO,
        };
      });
    },
    resurrectedTeens() {
      return this.teens.filter((teen) => teen.isResurrected);
    },
    claimableMeth() {
      let totalMeth = 0;
      this.genesis.forEach((token) => {
        totalMeth += token.claimableMeth;
      });
      this.gods.forEach((token) => {
        totalMeth += token.claimableMeth;
      });
      this.teens.forEach((teen) => {
        if (!teen.isResurrected) return;
        totalMeth += teen.claimableMeth;
      });
      return totalMeth;
    },
    allTokens() {
      return {
        genesis: this.genesis,
        gods: this.gods,
        teens: this.teens,
        orbs: this.orbs,
        incubators: this.incubators,
        methMachines: this.methMachines,
        teenResurrections: this.teenResurrections,
        breedingReplenishments: this.breedingReplenishments,
        botbOG: this.botbOG,
        botbEVO: this.botbEVO,
        botbBears: this.botbBears,
        apes: this.apes,
        partners: this.partners,
        apes: this.apes,
      };
    },
    botbTokens() {
      return [this.botbOG, this.botbEVO, this.botbBears];
    },
    totalClaimableMeth() {
      return this.claimableMeth;
    },
    isCollectionEmpty() {
      return (
        !this.genesis.length &&
        !this.teens.length &&
        !this.gods.length &&
        !this.orbs.length &&
        !this.incubators.length &&
        !this.methMachines.length &&
        !this.teenResurrections.length &&
        !this.breedingReplenishments.length &&
        !this.botbOG.length &&
        !this.botbEVO.length &&
        !this.botbBears.length &&
        !this.apes.length &&
        !this.apeTraits.length &&
        !Object.keys(this.partners).length
      );
    },

    collectionWallet() {
      return useWeb3Store().collectionWallet;
    },
  },
  actions: {
    async fetchApeTraits() {
      try {
        this.apeTraits = await fetchApeTraits();
      } catch (err) {
        captureError(err);
      }
    },
    async fetchApes() {
      try {
        this.apes = await fetchApes();
      } catch (err) {
        captureError(err);
      }
    },
    async fetchCollection(silent) {
      if (!silent) {
        this.isLoading = true;
      }
      try {
        const {
          teens,
          partners,
          genesis,
          gods,
          botb,
          teenResurrections,
          breedingReplenishments,
          incubators,
          orbs,
          methMachines,
          apes,
        } = await fetchCollection();
        this.teens = teens;
        this.genesis = genesis;
        this.gods = gods;
        this.botbEVO = botb.botbEVO;
        this.botbOG = botb.botbOG;
        this.botbBears = botb.botbBears;
        this.partners = partners;
        this.incubators = incubators;
        this.orbs = orbs;
        this.teenResurrections = teenResurrections;
        this.methMachines = methMachines;
        this.breedingReplenishments = breedingReplenishments;
        this.apes = apes;
        useSentry().Sentry.setTag(
          "bap_asset_count",
          Object.keys(this.allTokens).reduce((sum, key) => {
            return (sum +=
              key !== "partners" ? this.allTokens[key].length || 0 : 0);
          }, 0)
        );
        useSentry().Sentry.setTag(
          "community_asset_count",
          Object.keys(this.partners).reduce((sum, key) => {
            return (sum += this.partners[key].length || 0);
          }, 0)
        );
      } catch (err) {
        useToast().error({
          text: "Couldn't load your collection, please try again",
        });
        captureError(err);
      }
      this.isLoading = false;
    },
  },
});
